import React, { useLayoutEffect } from 'react';
import { useParams } from 'react-router-dom';
import i18next from 'i18next';

import { BASE_LINK_LANGUAGE, linkLanguage } from 'core/services/editLanguage';
import { setLearnerLanguage } from 'core/slices/translation';
import { useAppDispatch } from 'core/store/hooks';
import { translationsJson } from 'locales/i18n';

/**
 * A learner link's language: `/fr/{token}` opens the course in French, and
 * `/en/{token}`, every link made so far, in the language it was written in.
 *
 * The segment was always "en" and meant nothing. It now picks which
 * translation each module loads in; a module with none in that language plays
 * as written. The app's own labels follow too, where there are any in it.
 *
 * The route is `:lang/:token`, which would otherwise take any two-part path
 * nothing else claims, so a segment that isn't a language code is a 404
 * rather than a player looking up a token that doesn't exist.
 */
export const LanguageLink: React.FC<{ notFound: React.ReactNode; children: React.ReactNode }> = ({
  notFound,
  children,
}) => {
  const { lang } = useParams<{ lang?: string }>();
  const language = linkLanguage(lang);
  const dispatch = useAppDispatch();

  // Ahead of the player's own effects, so its first module read is already in the language.
  useLayoutEffect(() => {
    if (!language) return;

    const base = language === BASE_LINK_LANGUAGE;
    dispatch(setLearnerLanguage(base ? null : language));

    // "en" was on every link before it meant anything, so it leaves the labels
    // in whatever language the browser asked for, as it always has.
    const ui = language.split('-')[0];
    if (!base && ui in translationsJson && i18next.language !== ui) i18next.changeLanguage(ui);
  }, [dispatch, language]);

  return <>{language ? children : notFound}</>;
};

export default LanguageLink;
